
<script setup lang="ts">

import { ref } from 'Vue'

const props = defineProps<{
	form_id: string | number
	field_label: string
	description: string
}>()

const selectedParts = ref<SelectedPart[]>([])

const form_id = props.form_id
const field_label = props.field_label
const description = props.description

const form = document.getElementById(`gform_${form_id}`)

function checkIt() {
	if (!form) {
		return;
	}

	const fields = form.querySelectorAll<HTMLInputElement>('.gf-garage-part-picker-field__radio:checked:not(.gf_placeholder):not(.gc_default)')
	selectedParts.value = []

	for (var i = 0; i < fields.length; i++) {
		const field = fields[i]

		if ( field.value ) {
			const selectedPart: SelectedPart = {
				label: undefined !== field.dataset.label ? field.dataset.label : '',
				prodNo: undefined !== field.dataset.prodNo ? field.dataset.prodNo : '',
				description: undefined !== field.dataset.description ? field.dataset.description : '',
				imageUrl: undefined !== field.dataset.imageUrl ? field.dataset.imageUrl : '',
				imageWidth: undefined !== field.dataset.imageWidth ? field.dataset.imageWidth : '',
				imageHeight: undefined !== field.dataset.imageHeight ? field.dataset.imageHeight : '',
				value: field.value,
				buttonId: undefined !== field.dataset.buttonId ? field.dataset.buttonId : '',
			}

			selectedParts.value.push(selectedPart)
		}
	}
}

function onClick(selectedPart: SelectedPart) {
	document.getElementById(selectedPart.buttonId)?.click()
}

checkIt()

if (form) {
	form.addEventListener('change', function (evt) {
		checkIt()
	});
}
</script>

<template>
	<div v-if="Boolean(selectedParts.length)" class="garage-parts-customized">
		<div class="garage-parts-customized__head">
			<h2 class="garage-parts-customized__heading">{{ field_label }}</h2>
		</div>
		<ul class="garage-parts-customized__items">
			<li v-for="(selectedPart, index) in selectedParts" class="garage-parts-customized__item" :key="index">
				<div class="garage-parts-customized__radio-figure">
					<div class="garage-parts-customized__radio-figure-inner">
						<img v-if="Boolean(selectedPart.imageUrl)" class="garage-parts-customized__radio-img" :src="selectedPart.imageUrl" :width="selectedPart.imageWidth" :height="selectedPart.imageHeight"/>
					</div>
				</div>
				<h3 class="garage-parts-customized__label">{{selectedPart.label}}</h3>
				<div class="garage-parts-customized__description" v-html="selectedPart.description"></div>
				<div class="garage-parts-customized__prod-no">Produktnummer: {{ selectedPart.prodNo }}</div>
				<div class="garage-parts-customized__button-wrapper">
					<button class="garage-parts-customized__button button is-style-outline" @click="onClick(selectedPart)" type="button">Endre</button>
				</div>
			</li>
		</ul>
		<div v-if="Boolean(description)" class="garage-parts-customized__description">{{ description }}</div>
	</div>
</template>

<style lang="scss" scoped>
.garage-parts-customized {
	margin-bottom: 54px;

	&__items {
		display: grid;
		grid-template-columns: 1fr;
		grid-column-gap: 2%;
		grid-row-gap: 16px;
		margin-bottom: 0;

		@media (min-width: 1024px) {
			grid-template-columns: 1fr 1fr;
		}
	}

	&__item {
		background: #eef3f1;
		border: 1px solid #55806b;
		display: grid;
		grid-template-columns: 40% auto;
		grid-auto-rows: minmax(10px, auto) max-content max-content max-content max-content minmax(10px, auto);
		grid-template-areas:
			"figure ."
			"figure label"
			"figure prod-no"
			"figure value"
			"figure button"
			"figure .";
		column-gap: 10px;
		padding-right: 10px;

		@media (min-width: 768px) {
			grid-template-columns: 30% auto max-content;
			grid-auto-rows: minmax(30px, auto) max-content max-content max-content minmax(30px, auto);
			grid-template-areas:
				"figure . ."
				"figure label button"
				"figure value button"
				"figure prod-no button"
				"figure . .";
			column-gap: 20px;
			padding-right: 20px;
		}
	}

	&__radio-figure {
		grid-area: figure;
		width: 100%;
		background-color: #fff;
		position: relative;
	}

	&__radio-figure-inner {
		width: 100%;

		&::after {
			content: "";
			width: 100%;
			display: block;
			padding-bottom: 75%;
		}
	}

	&__radio-img {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		display: block;
		width: 100%;
		height: 100%;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	&__label {
		grid-area: label;
		margin-top: 0;
		font-family: "Realist";
		font-style: normal;
		font-weight: 600;
		line-height: 1.4;
		text-transform: capitalize;
		margin-bottom: 0.25rem;
		font-size: var(--wp--preset--font-size--medium);

		@media (min-width: 768px) {
			font-size: var(--wp--preset--font-size--large);
		}
	}

	&__prod-no {
		grid-area: prod-no;
		font-size: var(--wp--preset--font-size--tiny);
		font-weight: 600;
		color: #6b6b6b;
	}

	&__description {
		grid-area: value;
		margin-top: 0;
		margin-bottom: 0.5rem;
		line-height: 1.4;
		font-size: var(--wp--preset--font-size--medium);

		@media (min-width: 768px) {
			font-size: var(--wp--preset--font-size--large);
		}

		:deep(p) {
			margin-top: 0;
			margin-bottom: 0;
			line-height: 1.4;
		}

		:deep(p:not(:last-child)) {
			margin-bottom: 0.5rem;
		}
	}

	&__button-wrapper {
		grid-area: button;
		display: flex;
		align-items: center;
		margin-top: 0.5rem;

		@media (min-width: 768px) {
			margin-top: 0;
		}
	}
}
</style>
